import { Button, message } from "antd";
import WholesaleInfoBadge from "./WholesaleInfoBadge";
import { Link } from "react-router-dom";
import NInputNumber from "components/Desktop/NInputNumber";
import { FC, useEffect, useMemo, useState } from "react";
import { IProductCard } from "types/product";
import useUserStore from "stores/user";
import useLoginModal from "hooks/useLoginModal";
import { useAddToCart } from "services/cart";
import { useAddFavorite, useDeleteFavorite } from "services/favorite";
import { useRequestProduct } from "services/product";
import useNotificationCenter from "hooks/useNotificationCenter";
import { HeartOutlined, HeartFilled } from "@ant-design/icons";
import { IMAGE_NOT_FOUND } from "constants/common";
import useIsDesktopStore from "stores/isDesktop";
import ProductImage from "components/ProductImage";

const ProductCard: FC<IProductCard> = ({
  id,
  genericName,
  name,
  images,
  productPrices,
  isFavorite,
  unit,
  stockQuantity,
  productCode,
}) => {
  const { isDesktop } = useIsDesktopStore();
  const { user } = useUserStore();
  const { loginModal, showLogin } = useLoginModal();
  const { notificationCenter, showNotification, hideNotification } =
    useNotificationCenter();
  const [productQty, setProductQty] = useState<number>(1);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [favoriteState, setFavoriteState] = useState<boolean>(isFavorite);
  const { addToCartAsync } = useAddToCart();
  const { addFavoriteAsync } = useAddFavorite();
  const { deleteFavoriteAsync } = useDeleteFavorite();
  const { requestProductAsync, isRequesting } = useRequestProduct();
  const onAddItemToCart = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (stockQuantity === 0) {
      return; // Do nothing if out of stock
    }

    if (user) {
      // add item to cart
      if (productQty > 0) {
        showNotification();
        await addToCartAsync({
          productId: id,
          quantity: productQty,
        });
        setTimeout(() => {
          hideNotification();
        }, 350);
      }
    } else {
      // open login modal
      showLogin();
    }
  };

  const handleOnChangeInput = (qty: number) => {
    setProductQty(qty);
  };

  useEffect(() => {
    if (favoriteState) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300); // Duration of the animation
      return () => clearTimeout(timer);
    }
  }, [favoriteState]);

  const price = useMemo(() => {
    if (productPrices.length > 1) {
      const priceList = productPrices.sort(
        (a, b) => (b.minQuantity || 0) - (a.minQuantity || 0)
      );
      const cheapestPrice = priceList.find(
        (price) => productQty >= (price.minQuantity || 0)
      );
      const fallbackPrice = priceList.at(-1);

      // Convert price to number before multiplication
      const finalPrice = Number(
        cheapestPrice?.price || fallbackPrice?.price || 0
      );
      return finalPrice * productQty;
    } else {
      const finalPrice = Number(productPrices[0]?.price || 0);
      return finalPrice * productQty;
    }
  }, [productQty]);
  console.log("images", images);
  return (
    <div
      className="w-auto xl:w-[215px] p-2 sm:p-4 bg-white flex gap-3 flex-col relative product-card"
      style={{
        boxShadow: "1px 4px 13px 0px #7B2FE612",
        border: "1px solid #00000012",
      }}
    >
      <div className="block md:hidden">
        <WholesaleInfoBadge priceList={productPrices} unit={unit} />
      </div>
      <div
        className={`absolute right-3 cursor-pointer text-primary z-10 ${
          isAnimating ? "animate" : ""
        }`}
        onClick={() => {
          if (favoriteState) {
            deleteFavoriteAsync(id);
            setFavoriteState(false);
          } else {
            setFavoriteState(true);
            addFavoriteAsync(id);
          }
        }}
      >
        {favoriteState ? (
          <HeartFilled style={{ fontSize: "24px" }} />
        ) : (
          <HeartOutlined style={{ fontSize: "24px" }} />
        )}
      </div>
      <Link
        to={`/product/${id}`}
        className="flex flex-col justify-between h-full"
      >
        <div>
          <div className="text-center relative">
            {isDesktop ? (
              <div className="relative">
                <ProductImage
                  preview={false}
                  imageUrl={images[0] || ""}
                  productCode={productCode}
                  width={180}
                  height={180}
                  alt=""
                  className="object-contain"
                  fallback={IMAGE_NOT_FOUND}
                />
                {stockQuantity === 0 && (
                  <div className="absolute w-1/2 h-1/2 bg-[#35373cba] flex items-center justify-center rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="text-white">สินค้าหมด</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <ProductImage
                  preview={false}
                  imageUrl={images[0] || ""}
                  productCode={productCode}
                  width="100%"
                  height={160}
                  alt=""
                  className="object-contain"
                  fallback={IMAGE_NOT_FOUND}
                />
                {stockQuantity === 0 && (
                  <div className="absolute w-1/2 h-1/2 bg-[#35373cba] flex items-center justify-center rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="text-white">สินค้าหมด</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="hidden md:block">
            <WholesaleInfoBadge priceList={productPrices} unit={unit} />
          </div>
          <div className="text-base font-semibold mt-0 md:mt-2 line-clamp-2 break-words">
            {name}
          </div>
          {isDesktop && (
            <div className="text-sm line-clamp-1 break-words">
              {genericName}
            </div>
          )}
        </div>
        <div>
          {/* Desktop layout */}
          <div className="hidden md:flex justify-between text-primary items-center mt-4 text-center">
            <NInputNumber value={productQty} onChange={handleOnChangeInput} />
            <div className="font-semibold">฿{price.toLocaleString()}</div>
          </div>

          {/* Mobile layout - price above input */}
          <div className="flex flex-col md:hidden mt-2 text-center">
            <div className="font-semibold text-primary mb-3">
              ฿{price.toLocaleString()}
            </div>
            <div className="m-auto">
              <NInputNumber value={productQty} onChange={handleOnChangeInput} />
            </div>
          </div>

          {stockQuantity > 0 ? (
            <Button
              block
              type="primary"
              size="large"
              className="mt-4 sm:mt-6"
              onClick={onAddItemToCart}
            >
              <div className="flex justify-evenly w-full text-sm sm:text-base">
                <span>เพิ่มลงตะกร้า</span>
              </div>
            </Button>
          ) : (
            <div className="mt-4 sm:mt-6 text-center">
              <Button
                block
                type="default"
                size="large"
                loading={isRequesting}
                onClick={async (event) => {
                  event.preventDefault();
                  if (!user) {
                    // open login modal if user is not logged in
                    showLogin();
                    return;
                  }
                  try {
                    await requestProductAsync(id.toString());
                    message.success(
                      "ขอบคุณที่แจ้งความต้องการสินค้า เราจะแจ้งให้ทราบเมื่อมีสินค้า"
                    );
                  } catch (error) {
                    message.error("เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง");
                    console.error("Error requesting product:", error);
                  }
                }}
                className="bg-white text-secondary-600 border-secondary-600"
              >
                <div className="flex justify-evenly w-full text-sm sm:text-base">
                  <span>ต้องการสินค้านี้</span>
                </div>
              </Button>
            </div>
          )}
        </div>
      </Link>
      {loginModal}
      {notificationCenter}
    </div>
  );
};

export default ProductCard;
